import React, { useMemo, useRef, useState, createElement } from 'react';
import { View, Text, Pressable, TextInput } from '../lib/react-native-compat';
import { resolveSystemPrompt, captureUserPrompt } from './AISummary';

// A chat box that sits inside a document modal — the Pre-Export Checks modal
// on a billing summary, the day/shift preview on Time Card Approval — with
// the document's own data already in front of it, so the person about to send
// something to the client can ask for a revision in words instead of hunting
// for the control that makes it.
//
// It is not a general assistant. Everything it can actually change is handed
// in by the host modal as an `actions` catalog, and the catalog is what gets
// described to the model — so the chat can only ever propose an edit the page
// already knows how to make, and a proposal is inert until someone clicks
// Apply. Anything outside the catalog comes back as advice naming the screen
// where the fix lives.
//
// Wire protocol: the model answers in prose, and puts any concrete change it
// wants to make in a fenced ```apply block holding a JSON array of edits.
// parseProposals pulls those out; the prose is what gets rendered. The block
// format is defined here (in the code-built context turn), NOT in the
// server-editable system prompt — an admin rewriting the prompt's wording
// can't break the Apply buttons.

// Same env-aware base as db/company.js.
const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_ENV === 'test' || process.env.REACT_APP_ENV === 'dev')
        ? 'https://forecastingtestservice.azurewebsites.net/'
        : 'https://forecastingprodservice.azurewebsites.net/'
    : 'https://forecastingprodservice.azurewebsites.net/');

// The router's own default. gpt-4o-mini is the openaiChat front door's
// default and is not good enough at this — reconciling a hundred billing
// lines against a rate sheet — so the model is always sent explicitly.
export const DEFAULT_CHAT_MODEL = 'claude-sonnet-4-6';
const MODEL_CHOICES = [
    { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6' },
    { id: 'claude-opus-4-6', label: 'Opus 4.6' },
    { id: 'claude-haiku-4-5', label: 'Haiku 4.5' },
    { id: 'azure/gpt-4o-mini', label: 'GPT-4o mini' },
];

// ── Proposal parsing ──
// A fenced block tagged `apply` holding either a JSON array of edits or a
// single edit object. Everything else in the reply is prose. Tolerant by
// design: a malformed block is dropped rather than throwing, because a model
// that fumbles its JSON should cost the reader a button, not the answer.
const APPLY_FENCE = /```[ \t]*apply[ \t]*\r?\n([\s\S]*?)```/g;

export const parseProposals = (text) => {
    const raw = String(text || '');
    const out = [];
    let prose = '';
    let last = 0;
    APPLY_FENCE.lastIndex = 0;
    let m = APPLY_FENCE.exec(raw);
    while (m) {
        prose += raw.slice(last, m.index);
        last = m.index + m[0].length;
        try {
            const parsed = JSON.parse(m[1]);
            (Array.isArray(parsed) ? parsed : [parsed]).forEach((p) => {
                if (p && typeof p === 'object' && p.type) out.push(p);
            });
        } catch (e) { /* unparseable block — the prose still stands */ }
        m = APPLY_FENCE.exec(raw);
    }
    prose += raw.slice(last);
    return { prose: prose.trim(), proposals: out };
};

// While a reply is still streaming an apply block may be half-written. Show
// the prose up to it and hold the rest back — a reader should never watch
// raw JSON type itself out.
const streamProse = (text) => {
    const { prose } = parseProposals(text);
    const open = prose.search(/```[ \t]*apply/);
    return open === -1 ? prose : prose.slice(0, open).trim();
};

// ── Light markdown ──
// The prompts ask for short prose and bullets, so this handles exactly that:
// **bold** inline, `-`/`*` bullets, blank-line paragraphs. Anything heavier
// renders as the plain text it is.
const inlineBold = (s, key) => {
    const parts = String(s).split(/\*\*(.+?)\*\*/g);
    return parts.map((p, i) => (i % 2
        ? <Text key={`${key}b${i}`} style={{ fontWeight: '700' }}>{p}</Text>
        : <Text key={`${key}t${i}`}>{p}</Text>));
};

const Prose = ({ text, color }) => {
    const lines = String(text || '').split('\n');
    return lines.map((ln, i) => {
        const t = ln.trim();
        if (!t) return <View key={i} style={{ height: 5 }} />;
        const bullet = /^[-*•]\s+/.test(t);
        return (
            <Text
                key={i}
                style={{
                    fontSize: 12, lineHeight: 17, color: color || '#1e293b',
                    paddingLeft: bullet ? 10 : 0, marginTop: i ? 1 : 0,
                }}
            >
                {bullet ? '· ' : ''}{inlineBold(bullet ? t.replace(/^[-*•]\s+/, '') : t, `${i}`)}
            </Text>
        );
    });
};

const Spin = () => createElement('span', {
    style: {
        display: 'inline-block', width: 11, height: 11, borderRadius: '50%',
        border: '2px solid #cbd5e1', borderTopColor: '#0f172a',
        animation: 'baichatspin 0.7s linear infinite',
    },
});

// ── The context turn ──
// Built here, every send, so the model's picture of the document is current
// after an Apply rather than whatever it was when the modal opened.
const buildContextTurn = (context, actions) => {
    const catalog = Object.entries(actions || {}).map(([type, a]) => (
        `- ${type}(${a.args || ''}) — ${a.desc}`
    ));
    // A thunk, so the document is read at send time rather than whenever the
    // host last rendered — after an Apply the next question has to be asked
    // of the document as it stands, not the one that raised the question.
    const doc = typeof context === 'function' ? context() : context;
    return [
        'DOCUMENT — what the PDF would say right now:',
        JSON.stringify(doc),
        '',
        catalog.length
            ? [
                'CHANGES YOU CAN PROPOSE. These are the only edits this screen can make. To propose one,',
                'end your reply with a fenced block tagged apply holding a JSON array; each entry is',
                '{"type": <one of the types below>, "label": <what it does, in one short phrase a biller reads>, ...its arguments}.',
                'Reference rows by the "i" index they carry in the document above, never by name alone.',
                'Propose an edit only when the user asked for a change or agreed to one you suggested —',
                'a question about the document is answered in prose with no apply block. Never invent a',
                'type that is not listed. For anything you cannot change from here, say plainly what to',
                'fix and which screen owns it.',
                '',
                ...catalog,
                '',
                // Drawn from the real catalog so the shape shown is always a
                // type that exists here — a hardcoded example is the surest
                // way to teach the model an action this screen does not have.
                'Example:',
                '```apply',
                `[{"type":"${Object.keys(actions)[0]}", ...its arguments, "label":"what it does, in a few words"}]`,
                '```',
            ].join('\n')
            : 'This screen cannot change anything — answer in prose and say which screen owns each fix.',
    ].join('\n');
};

/**
 * @param section  server-editable system-prompt section id (see AISummary)
 * @param context  the document as the PDF would print it — an object, or a
 *                 thunk returning one, which is read fresh on every send
 * @param actions  { [type]: { desc, args, apply(proposal) -> string|Promise } }
 * @param onApplied fired after a successful apply, so the host can re-run its checks
 * @param locked   read-only (a locked version) — the box still answers, Apply is hidden
 * @param hint     placeholder text for the input
 */
const BillingAiChat = ({ section, context, actions, onApplied, locked, hint, title }) => {
    const [turns, setTurns] = useState([]); // { role, text, proposals?, state? }
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [model, setModel] = useState(DEFAULT_CHAT_MODEL);
    const [showModels, setShowModels] = useState(false);
    const [applied, setApplied] = useState({}); // "<turn>:<idx>" -> 'run'|'done'|error string
    const abortRef = useRef(null);
    const scrollRef = useRef(null);

    const live = useMemo(() => ({ context, actions }), [context, actions]);

    const send = async (textIn) => {
        const q = String(textIn == null ? input : textIn).trim();
        if (!q || busy) return;
        setInput('');
        setErr('');
        setBusy(true);
        setStreaming('');
        const history = [...turns, { role: 'user', text: q }];
        setTurns(history);

        const ctxTurn = buildContextTurn(live.context, live.actions);
        // The admin AI tab reads this back to show what the model is actually
        // being sent for the section.
        captureUserPrompt(section, ctxTurn);

        const controller = new AbortController();
        abortRef.current = controller;
        try {
            const system = await resolveSystemPrompt(section);
            // The document rides on the first user turn and is refreshed each
            // send; the rest of the conversation is verbatim.
            const messages = [
                { role: 'system', content: system },
                { role: 'user', content: ctxTurn },
                { role: 'assistant', content: 'Understood — I have the document and the changes I can propose.' },
                ...history.map((t) => ({ role: t.role, content: t.text })),
            ];
            const res = await fetch(`${API_BASE}api/openaiChat`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ messages, model, max_tokens: 4096, temperature: 0.2 }),
                signal: controller.signal,
            });
            if (!res.ok) throw new Error(`the assistant returned ${res.status}`);

            let acc = '';
            const take = (chunk) => {
                acc += chunk;
                setStreaming(streamProse(acc));
            };
            const consume = (payload) => {
                if (payload === '[DONE]') return;
                try {
                    const p = JSON.parse(payload);
                    if (p.error) throw new Error(p.error);
                    if (p.content) take(p.content);
                } catch (e) {
                    if (e instanceof SyntaxError) return; // partial/garbled frame
                    throw e;
                }
            };

            // jsdom has no streaming body — the tests (and any browser that
            // buffers the response) still get the whole answer this way.
            if (!res.body || !res.body.getReader) {
                String(await res.text()).split('\n').forEach((ln) => {
                    const t = ln.trim();
                    if (t.startsWith('data: ')) consume(t.slice(6));
                });
            } else {
                const reader = res.body.getReader();
                const dec = new TextDecoder();
                let buf = '';
                for (;;) {
                    const { done, value } = await reader.read(); // eslint-disable-line no-await-in-loop
                    if (done) break;
                    buf += dec.decode(value, { stream: true });
                    const lines = buf.split('\n');
                    buf = lines.pop() || '';
                    lines.forEach((ln) => {
                        const t = ln.trim();
                        if (t.startsWith('data: ')) consume(t.slice(6));
                    });
                }
            }

            const { prose, proposals } = parseProposals(acc);
            setTurns((p) => [...p, {
                role: 'assistant',
                text: prose || '(no answer came back)',
                proposals: locked ? [] : proposals,
            }]);
        } catch (e) {
            if (e.name === 'AbortError') setTurns((p) => p.slice(0, -1));
            else setErr(String(e?.message || e));
        } finally {
            setStreaming('');
            setBusy(false);
            abortRef.current = null;
            if (scrollRef.current) {
                try { scrollRef.current.scrollTop = scrollRef.current.scrollHeight; } catch (e) { /* ignore */ }
            }
        }
    };

    const apply = async (turnIdx, propIdx, p) => {
        const k = `${turnIdx}:${propIdx}`;
        if (applied[k] === 'run' || applied[k] === 'done') return;
        const spec = (live.actions || {})[p.type];
        if (!spec) { setApplied((s) => ({ ...s, [k]: `this screen has no "${p.type}" action` })); return; }
        setApplied((s) => ({ ...s, [k]: 'run' }));
        try {
            await spec.apply(p);
            setApplied((s) => ({ ...s, [k]: 'done' }));
            if (onApplied) onApplied(p);
        } catch (e) {
            setApplied((s) => ({ ...s, [k]: String(e?.message || e) }));
        }
    };

    const renderProposal = (p, turnIdx, i) => {
        const k = `${turnIdx}:${i}`;
        const st = applied[k];
        const spec = (live.actions || {})[p.type];
        const known = Boolean(spec);
        return (
            <View
                key={k}
                style={{
                    flexDirection: 'row', alignItems: 'center', marginTop: 5,
                    borderWidth: 1, borderColor: st === 'done' ? '#bbf7d0' : '#e2e8f0', borderRadius: 4,
                    backgroundColor: st === 'done' ? '#f0fdf4' : '#fff',
                    paddingTop: 5, paddingBottom: 5, paddingLeft: 8, paddingRight: 7,
                }}
            >
                <View style={{ flexGrow: 1, flexShrink: 1, paddingRight: 8 }}>
                    <Text style={{ fontSize: 11.5, color: '#0f172a', fontWeight: '600' }}>
                        {p.label || p.type}
                    </Text>
                    {!known ? (
                        <Text style={{ fontSize: 10.5, color: '#b45309', marginTop: 1 }}>
                            {`Not something this screen can change (${p.type}) — do it where it lives.`}
                        </Text>
                    ) : typeof st === 'string' && st !== 'run' && st !== 'done' ? (
                        <Text style={{ fontSize: 10.5, color: '#b91c1c', marginTop: 1 }}>{st}</Text>
                    ) : null}
                </View>
                {known ? (
                    <Pressable
                        onPress={() => apply(turnIdx, i, p)}
                        style={{
                            borderWidth: 1, borderColor: st === 'done' ? '#bbf7d0' : '#0f172a', borderRadius: 4,
                            backgroundColor: st === 'done' ? '#f0fdf4' : '#0f172a',
                            paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10,
                            cursor: st === 'done' ? 'default' : 'pointer',
                        }}
                    >
                        {st === 'run' ? <Spin /> : (
                            <Text style={{ fontSize: 11, fontWeight: '700', color: st === 'done' ? '#166534' : '#fff' }}>
                                {st === 'done' ? '✓ Applied' : 'Apply'}
                            </Text>
                        )}
                    </Pressable>
                ) : null}
            </View>
        );
    };

    const empty = !turns.length && !streaming;

    return (
        <View style={{ borderTopWidth: 1, borderColor: '#e2e8f0', paddingTop: 10 }}>
            {createElement('style', null, '@keyframes baichatspin { to { transform: rotate(360deg); } }')}
            <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
                <Text style={{ fontSize: 11, fontWeight: '700', color: '#334155', letterSpacing: 0.4 }}>
                    {(title || 'Ask about this document').toUpperCase()}
                </Text>
                <View style={{ flexGrow: 1 }} />
                <View style={{ position: 'relative' }}>
                    <Pressable
                        onPress={() => setShowModels((v) => !v)}
                        style={{ paddingTop: 2, paddingBottom: 2, paddingLeft: 6, paddingRight: 6, cursor: 'pointer' }}
                    >
                        <Text style={{ fontSize: 10, color: '#94a3b8' }}>
                            {`${(MODEL_CHOICES.find((m) => m.id === model) || {}).label || model} ▾`}
                        </Text>
                    </Pressable>
                    {showModels ? (
                        <View style={{
                            position: 'absolute', bottom: '100%', right: 0, marginBottom: 3, zIndex: 30,
                            backgroundColor: '#fff', borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4,
                            boxShadow: '0 6px 18px rgba(15,23,42,.14)', minWidth: 130,
                        }}>
                            {MODEL_CHOICES.map((m) => (
                                <Pressable
                                    key={m.id}
                                    onPress={() => { setModel(m.id); setShowModels(false); }}
                                    style={{
                                        paddingTop: 5, paddingBottom: 5, paddingLeft: 10, paddingRight: 10, cursor: 'pointer',
                                        backgroundColor: m.id === model ? '#f1f5f9' : '#fff',
                                    }}
                                >
                                    <Text style={{ fontSize: 11, color: '#334155' }}>{m.label}</Text>
                                </Pressable>
                            ))}
                        </View>
                    ) : null}
                </View>
            </View>

            {!empty ? (
                <div
                    ref={scrollRef}
                    style={{ maxHeight: 250, overflowY: 'auto', marginBottom: 7, paddingRight: 4 }}
                >
                    {turns.map((t, ti) => (
                        <View key={ti} style={{ marginBottom: 8 }}>
                            {t.role === 'user' ? (
                                <View style={{ alignItems: 'flex-end' }}>
                                    <View style={{
                                        backgroundColor: '#f1f5f9', borderRadius: 6, maxWidth: '86%',
                                        paddingTop: 5, paddingBottom: 5, paddingLeft: 9, paddingRight: 9,
                                    }}>
                                        <Text style={{ fontSize: 12, lineHeight: 17, color: '#0f172a' }}>{t.text}</Text>
                                    </View>
                                </View>
                            ) : (
                                <View>
                                    <Prose text={t.text} />
                                    {(t.proposals || []).map((p, i) => renderProposal(p, ti, i))}
                                </View>
                            )}
                        </View>
                    ))}
                    {streaming ? <Prose text={streaming} color="#334155" /> : null}
                    {busy && !streaming ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <Spin />
                            <Text style={{ fontSize: 11, color: '#94a3b8', marginLeft: 7 }}>Reading the document…</Text>
                        </View>
                    ) : null}
                </div>
            ) : null}

            {err ? (
                <Text style={{ fontSize: 11, color: '#b91c1c', marginBottom: 6 }}>{err}</Text>
            ) : null}

            <View style={{ flexDirection: 'row', alignItems: 'flex-end' }}>
                <TextInput
                    value={input}
                    onChangeText={setInput}
                    multiline
                    numberOfLines={2}
                    placeholder={hint || 'Ask for a revision, or what looks wrong…'}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    style={{
                        flexGrow: 1, flexShrink: 1, fontSize: 12, lineHeight: 1.45, minHeight: 34,
                        borderWidth: 1, borderColor: '#e2e8f0', borderRadius: 4,
                        paddingTop: 7, paddingBottom: 7, paddingLeft: 9, paddingRight: 9,
                    }}
                />
                <Pressable
                    onPress={busy ? () => abortRef.current && abortRef.current.abort() : () => send()}
                    style={{
                        marginLeft: 8, marginBottom: 1, borderRadius: 4,
                        backgroundColor: busy ? '#fff' : '#0f172a',
                        borderWidth: 1, borderColor: busy ? '#e2e8f0' : '#0f172a',
                        paddingTop: 7, paddingBottom: 7, paddingLeft: 13, paddingRight: 13, cursor: 'pointer',
                    }}
                >
                    <Text style={{ fontSize: 12, fontWeight: '600', color: busy ? '#475569' : '#fff' }}>
                        {busy ? 'Stop' : 'Send'}
                    </Text>
                </Pressable>
            </View>
            {empty ? (
                <Text style={{ fontSize: 10.5, color: '#94a3b8', marginTop: 5 }}>
                    {locked
                        ? 'This version is locked — the assistant can look and advise, but nothing here can be changed.'
                        : 'It can see every line on this document and propose changes for you to apply.'}
                </Text>
            ) : null}
        </View>
    );
};

export default BillingAiChat;
